import { Skeleton, Box, Typography } from "@mui/material";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { useState, useEffect, useRef, useContext } from "react";
import DataGridKeywordExact from "src/components/data-grid/activations/data-grid-keyword-exact";
import ModalCsvImport from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-csv-import";
import Iconify from "src/components/iconify";
import { CountryBar, CountryBar as CountryFilter } from 'src/components/country-bar';
import { useCountryData } from "src/components/visibility";
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";
import { useGetKeywordsQuery } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ActivationsKeywordExact() {
    const workspace: any = useCurrentWorkspace();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sortModel, setSortModel] = useState<any>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: []
    });
    const [localPatches, setLocalPatches] = useState({});
    const [openCsvImport, setOpenCsvImport] = useState(false);
    const { countriesData, isLoading } = useCountryData();
    const [selectedCountry, setSelectedCountry]: any = useState<string | null>(null);
    const ciContext = useContext(VisibilityContext);
    const prefilledFromQueryRef = useRef<boolean>(false);

    const [selectedDateRange, setSelectedDateRange] = useState({
        label: "Last 7 Days",
        value: "last7d"
    });

    const [selectedCompareDate, setSelectedCompareDate] = useState({
        label: "Prev. Period",
        value: "previous_period"
    });

    const handleDateRangeChange = (newRange: { label: string; value: string }) => {
        setSelectedDateRange(newRange);
    }

    const handleCompareDateChange = (newRange: { label: string; value: string }) => {
        setSelectedCompareDate(newRange);
    }

    const { data: keywordsExact, isFetching, refetch } = useGetKeywordsQuery(
        {
            client: workspace?.bqTable,
            page: page + 1,
            page_size: pageSize,
            workspace_id: workspace?.id,
            match_type: "EXACT",
            country: selectedCountry,
            sort: sortModel,
            filters: filterModel,
            date: selectedDateRange?.value || "last30d",
            compareDate: selectedCompareDate?.value

        },
        {
            skip: !workspace || !selectedCountry, refetchOnMountOrArgChange: true
        }
    );

    useEffect(() => {
        setPage(0);
    }, [sortModel, filterModel]);

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
            return;
        }

        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country') || params.get('c');

        if (codeParam && countriesData.length > 0) {
            const mapped = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                prefilledFromQueryRef.current = true;
                setSelectedCountry(mapped);
                return;
            }
        }

        if (!prefilledFromQueryRef.current && !selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }

    }, [ciContext?.selectedCountry, location.search, countriesData]);

    const isReady = !!workspace && countriesData.length > 0 && selectedCountry !== null;

    return (
        <>
            <Box>
                <CountryBar
                    countries={countriesData}
                    selectedCountry={selectedCountry}
                    onCountryChange={setSelectedCountry}
                    includeAll
                    loading={isLoading}
                    stickyTop={110}
                    backgroundColor="background.paper"
                    topGap={20}
                    contentPaddingX={"0px"}
                />

                <Box px={'24px'} mt={isLoading ? '24px' : (countriesData && countriesData?.length > 1) ? '24px' : '0px'}>
                    <Box
                        bgcolor="white"
                        sx={{
                            border: "1px solid rgba(145, 158, 171, 0.20)",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                            height: '720px'
                        }}
                    >
                        {!isReady || isFetching ? (
                            <Skeleton
                                variant="rectangular"

                                sx={{
                                    flex: 1,
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "8px",
                                }}
                            />
                        ) : (
                            <DataGridKeywordExact
                                refetch={refetch}
                                data={keywordsExact?.data ?? []}
                                rowCount={keywordsExact?.total ?? 0}
                                page={page}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                localPatches={localPatches}
                                setLocalPatches={setLocalPatches}
                                selectedDateRange={selectedDateRange}
                                selectedCountry={selectedCountry}
                                selectedCompareDate={selectedCompareDate}
                                onDateRangeChange={handleDateRangeChange}
                                onCompareDateChange={handleCompareDateChange}
                                onOpenCsvImport={() => setOpenCsvImport(true)}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
            {workspace && selectedCountry && (
                <ModalCsvImport
                    open={openCsvImport}
                    onClose={() => setOpenCsvImport(false)}
                    matchType="EXACT"
                    workspaceId={workspace.id}
                    client={workspace.bqTable}
                    country={selectedCountry}
                    filters={filterModel}
                />
            )}
        </>
    );
}